import { primeAudioSession, resumeAudio } from "@/lib/browser-hygiene";
import type { HornPreset } from "@/lib/audio/presets";
import type { PlayMode, VoiceMode } from "@/lib/audio/settings";

export type HornMeter = {
  env: number;
  bloom: number;
  effort: number;
  mute: number;
  stage: string;
  slur: number;
  level: number;
  freq: number;
};

export class GloamEngine {
  ctx: AudioContext | null = null;
  node: AudioWorkletNode | null = null;
  analyser: AnalyserNode | null = null;
  private gain: GainNode | null = null;
  private volume = 0.8;
  private voiceMode: VoiceMode = "lip";
  private playMode: PlayMode = "mono";
  /** Horn −1, today's lip 0, bass +1. The worklet already starts at 0. */
  private character = 0;
  private shade = 0.62;
  private generation = 0;
  private booting: Promise<void> | null = null;
  private meterHandler: ((meter: HornMeter) => void) | null = null;
  private errorHandler: (() => void) | null = null;

  async ensure() {
    // Resume inside the gesture, even while addModule is pending.
    if (this.ctx) void resumeAudio(this.ctx);
    if (this.node && this.ctx?.state !== "closed") return;
    if (!this.booting) {
      const pending = this.boot(this.generation);
      this.booting = pending;
      void pending
        .finally(() => {
          if (this.booting === pending) this.booting = null;
        })
        .catch(() => undefined);
    }
    await this.booting;
  }

  private async boot(generation: number) {
    if (!window.isSecureContext || !window.AudioContext)
      throw new Error("Web Audio requires HTTPS or localhost.");
    primeAudioSession();
    const ctx = new AudioContext();
    this.ctx = ctx;
    void resumeAudio(ctx);
    try {
      if (!ctx.audioWorklet) throw new Error("AudioWorklet is unavailable.");
      const base = new URL(import.meta.env.BASE_URL, document.baseURI);
      await ctx.audioWorklet.addModule(new URL("worklets/gloam-processor.js", base).href);
      if (generation !== this.generation || this.ctx !== ctx || ctx.state === "closed") {
        throw new Error("Audio startup was cancelled.");
      }
      const node = new AudioWorkletNode(ctx, "gloam-processor", {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [2],
      });
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.7;
      const gain = ctx.createGain();
      gain.gain.value = this.volume * 0.92;
      node.connect(gain);
      gain.connect(analyser);
      analyser.connect(ctx.destination);
      node.port.onmessage = (event: MessageEvent<HornMeter & { type?: string }>) => {
        if (event.data?.type === "meter") this.meterHandler?.(event.data);
      };
      node.onprocessorerror = () => {
        if (this.node !== node) return;
        this.errorHandler?.();
        void this.close();
      };
      this.node = node;
      this.gain = gain;
      this.analyser = analyser;
      if (this.voiceMode !== "placed") {
        node.port.postMessage({ type: "voiceMode", mode: this.voiceMode });
      }
      if (this.playMode === "poly") {
        node.port.postMessage({ type: "playMode", mode: "poly" });
      }
      if (this.character !== 0) {
        node.port.postMessage({ type: "character", value: this.character });
      }
      if (this.shade !== 0.62) {
        node.port.postMessage({ type: "shade", value: this.shade });
      }
    } catch (error) {
      if (this.ctx === ctx) this.ctx = null;
      if (ctx.state !== "closed") void ctx.close().catch(() => undefined);
      throw error;
    }
  }

  onMeter(handler: ((meter: HornMeter) => void) | null) {
    this.meterHandler = handler;
  }
  onError(handler: (() => void) | null) {
    this.errorHandler = handler;
  }
  setVolume(value: number) {
    if (!Number.isFinite(value)) return;
    this.volume = Math.max(0, Math.min(1, value));
    if (this.gain && this.ctx)
      this.gain.gain.setTargetAtTime(this.volume * 0.92, this.ctx.currentTime, 0.015);
  }
  setPreset(preset: HornPreset) {
    this.node?.port.postMessage({ type: "preset", preset });
  }
  setEffort(value: number) {
    this.node?.port.postMessage({ type: "effort", value });
  }
  setMute(value: number) {
    this.node?.port.postMessage({ type: "mute", value });
  }
  setBend(cents: number) {
    this.node?.port.postMessage({ type: "bend", cents });
  }
  setVoiceMode(mode: VoiceMode) {
    this.voiceMode = mode === "lip" ? "lip" : "placed";
    this.node?.port.postMessage({ type: "voiceMode", mode: this.voiceMode });
  }
  setPlayMode(mode: PlayMode) {
    this.playMode = mode === "poly" ? "poly" : "mono";
    this.node?.port.postMessage({ type: "playMode", mode: this.playMode });
  }
  /** Horn −1 through today's lip (0) to bass +1. Non-finite values are ignored. */
  setCharacter(value: number) {
    if (!Number.isFinite(value)) return;
    this.character = Math.max(-1, Math.min(1, value));
    this.node?.port.postMessage({ type: "character", value: this.character });
  }
  /** Per-bore lip control. 0.62 is that bore's home sound. Non-finite values are ignored. */
  setShade(value: number) {
    if (!Number.isFinite(value)) return;
    this.shade = Math.max(0, Math.min(1, value));
    this.node?.port.postMessage({ type: "shade", value: this.shade });
  }
  noteOn(freq: number, legato: boolean, velocity = 0.7, breathScale?: number) {
    // Omit the scale when it is full: the worklet then takes the keys/touch path unchanged.
    if (breathScale === undefined || breathScale === 1) {
      this.node?.port.postMessage({ type: "note", freq, legato, velocity });
    } else {
      this.node?.port.postMessage({ type: "note", freq, legato, velocity, breathScale });
    }
  }
  /** One chord tone. `id` is the input that owns it (key, finger, or MIDI note). */
  noteOnId(id: string, freq: number, legato: boolean, velocity = 0.7, breathScale?: number) {
    if (breathScale === undefined || breathScale === 1) {
      this.node?.port.postMessage({ type: "note", id, freq, legato, velocity });
    } else {
      this.node?.port.postMessage({ type: "note", id, freq, legato, velocity, breathScale });
    }
  }
  release() {
    this.node?.port.postMessage({ type: "release" });
  }
  releaseId(id: string) {
    this.node?.port.postMessage({ type: "release", id });
  }
  /** Cut one poly voice, including its bore tail (channel all-sound-off). */
  silenceId(id: string) {
    this.node?.port.postMessage({ type: "silence", id });
  }
  panic() {
    this.node?.port.postMessage({ type: "panic" });
  }

  async close() {
    ++this.generation;
    const ctx = this.ctx;
    const node = this.node;
    this.ctx = null;
    this.node = null;
    this.analyser = null;
    this.booting = null;
    this.gain?.disconnect();
    this.gain = null;
    if (node) {
      node.port.onmessage = null;
      node.onprocessorerror = null;
      node.disconnect();
      node.port.close();
    }
    if (ctx && ctx.state !== "closed") await ctx.close().catch(() => undefined);
  }
}
